import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { findFontForStyle, FontStyle } from '@bynder-studio/structured-text';
import useTextSelection from 'packages/hooks/useTextSelection';
import { fontsElementsSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import { fontStyleName } from 'packages/pages/brandFonts/components/styleName';
import type { AllFontsMap, ExtendedFont, ExtendedFontByFamily, FontFamily, FontsMapByFamily } from './types';

export default function useFonts() {
    const { fontIdData } = useTextSelection();
    const fonts: FontFamily[] = useSelector(fontsElementsSelector);
    const [fontId, isMixed, mixedValue] = fontIdData;

    const fontsList: { [fontName: string]: FontFamily } = useMemo(() => {
        const temp = {};

        fonts.forEach((font) => {
            temp[font.name] = font;
        });

        return temp;
    }, [fonts]);

    const [familyList, idToFont]: [FontsMapByFamily, AllFontsMap] = useMemo(() => {
        const tempFamilyList = {} as FontsMapByFamily;
        const tempIdToFont = {} as AllFontsMap;

        fonts.forEach((font) => {
            tempFamilyList[font.name] = font.fonts;
            font.fonts.forEach((f) => {
                const extended = {
                    ...f,
                    familyName: font.name,
                    name: fontStyleName(font, f),
                    style: f.style,
                    weight: f.weight,
                };

                // One shared object under every reference id, so two ids for the same face are `===`.
                f.referenceIds.forEach((referenceId) => {
                    tempIdToFont[referenceId.toString()] = extended;
                });
            });
        });

        return [tempFamilyList, tempIdToFont];
    }, [fonts]);

    const currentFont: ExtendedFontByFamily = useMemo(() => idToFont[String(fontId)], [idToFont, fontId]);

    const loadDefaultFontByFamily = (fonts): ExtendedFont =>
        (currentFont && findFontForStyle(fonts, currentFont.style)) ||
        findFontForStyle(fonts, FontStyle.REGULAR) ||
        fonts[0];

    return {
        fontId,
        isMixed,
        mixedValue,
        fonts,
        fontsList,
        familyList,
        currentFont,
        idToFont,
        loadDefaultFontByFamily,
    };
}
